import { parse } from "path";
import { json } from "stream/consumers";

export const APPROVAL_STATUS = {
    APPROVED: "APPROVED",
    PENDING: "PENDING",
    NOT_CREATED: "Approval Not Created",
    BUILD_COMPLETED: "BUILD_COMPLETED"
} as const;

/**
 * Resolves the final approval status based on matching approval data
 * @param matchingApproval - The approval data for a specific map pack
 * @returns The resolved approval status string
 */
const resolveApprovalStatus = (matchingApproval: ApprovalStatusItem | undefined): string => {
    if (!matchingApproval) {
        return APPROVAL_STATUS.NOT_CREATED;
    }

    // Check explicit status first
    if (matchingApproval.status?.trim()) {
        return matchingApproval.status;
    }

    // Check approval status field
    if (matchingApproval.approvalStatus?.trim()) {
        return matchingApproval.approvalStatus;
    }

    // Check approval rules
    if (matchingApproval.approvalRules && Array.isArray(matchingApproval.approvalRules)) {
        const allRulesSatisfied = matchingApproval.approvalRules.every((rule: ApprovalRule) => 
            rule.ruleSatisfied === true
        );
        return allRulesSatisfied ? APPROVAL_STATUS.APPROVED : APPROVAL_STATUS.PENDING;
    }

    // Default to pending if approval exists but no clear status
    return APPROVAL_STATUS.PENDING;
};

/**
 * Calculates total pending approvals from approval data
 * @param matchingApproval - The approval data for a specific map pack
 * @returns The total number of pending approvals
 */
const calculatePendingApprovals = (matchingApproval: ApprovalStatusItem | undefined): number => {
    if (!matchingApproval) {
        return 0;
    }

    // Use explicit total if available
    if (matchingApproval.totalPendingApprovals !== undefined) {
        return matchingApproval.totalPendingApprovals;
    }

    // Calculate from approval rules
    if (matchingApproval.approvalRules && Array.isArray(matchingApproval.approvalRules)) {
        return matchingApproval.approvalRules.reduce((sum: number, rule: ApprovalRule) => {
            const pending = rule.pendingApprovals ? rule.pendingApprovals.length : 0;
            return sum + pending;
        }, 0);
    }

    return 0;
};

// Type definitions for addApprovalStatus function
interface ApprovalRule {
    ruleName: string;
    ruleSatisfied: boolean;
    pendingApprovals?: string[];
}

interface ApprovalStatusItem {
    mapPackString: string;
    status?: string;
    approvalStatus?: string;
    approvalRules?: ApprovalRule[];
    totalPendingApprovals?: number;
}

interface ReleaseStatusItem {
    rsmRegion: string;
    serviceRegionOperation: string;
    service: string;
    spsMetadata: {
        stateId: string;
    };
    ttlInEpochSeconds: number;
    operation: string;
    validationCompletionEpochSeconds: number;
    status: string;
}

interface ValidationNestedItem {
    mapPackVersion: string;
    summaryId: string;
    validationSummary: string;
    validationStatus: string;
    approvers: string;
    approvalStatus?: string;
}

interface ValidationDataItem {
    rsmVersion: string;
    rsmControlVersion: string;
    trecsRecostingLink: string;
    stateID: string;
    approved: number | string;
    totalApprovers: number | string;
    MCM: string;
    release: string;
    releaseStatus?: string;
    totalPendingApprovals?: number;
    nestedItems: ValidationNestedItem[];
}

type ValidationData = ValidationDataItem[];
type ApprovalStatus = ApprovalStatusItem[] | { [key: string]: ApprovalStatusItem };
type ReleaseStatus = ReleaseStatusItem[];

/**
 * Reformats mappack release metrics to correspond with variables defined in table
 * @param releaseMetricsJSON data
 * @returns reformatted data
 */
export const reformatReleaseMetricsJSON = (releaseMetricsJSON: any) => {
    // MajorTom-NA-20250512100047183-1747113860-NA_DTC_V3_WITH_RISK_V1
    const reformattedData = releaseMetricsJSON.reduce((acc: any, item: any)=> {
        const splitCurrMappack = item.mapPack.split('-');
        const splitControlMappack = item.mapPackControl.split('-');
        const mapPack = splitCurrMappack[2] + '-' + splitCurrMappack[3];
        
        if (!acc[mapPack]) {
            
            acc[mapPack] = {
                rsmVersion: splitCurrMappack[2] || '',
                rsmControlVersion: splitControlMappack[2] || '',
                trecsRecostingLink: item.trecsRecostingLink || '',
                approved: '',
                totalApprovers: '',
                MCM: '',
                release: '',
                nestedItems: []
            };
        }
        const subItem = {
            mapPackVersion: item.mapPack || '',
            summaryId: item.summaryId || '',
            validationSummary: item.validationSummary || '',
            validationStatus: item.validationStatus || '',
            approvers: item.approvers || '',
            approvalStatus: item.approvalStatus || '',
        };
        acc[mapPack].nestedItems.push(subItem);
        return acc;
    }, {});
    

    const res = Object.values(reformattedData);
    return res

}

/**
 * Reformats bedrock summary to get rid of unnecessary metadata
 * @param bedrockSummary bedrock summary
 * @returns reformatted summary
 */
export const reformatBedrockSummary = (bedrockSummary: string) => {
    const classificationStart = bedrockSummary.indexOf('{\"mapPackClassification\":');
    const classificationEnd = bedrockSummary.indexOf('}', classificationStart) + 1;

    let classificationSummary = "";
    let mapPackClassification = "";
    if (classificationStart !== -1 && classificationEnd !== -1) {
        classificationSummary = bedrockSummary.slice(0, classificationStart);
        const classificationParsed = JSON.parse(bedrockSummary.slice(classificationStart, classificationEnd));
        mapPackClassification = classificationParsed.mapPackClassification;
    } else {
        console.error('Invalid classification JSON format');
    }

    const breachedStationsStart = bedrockSummary.indexOf('{\"breachedStations\":');
    const breachedStationsEnd = breachedStationsStart + bedrockSummary.slice(breachedStationsStart).lastIndexOf('}') + 1;

    let arrayStations = [];
    if (breachedStationsStart !== -1 && breachedStationsEnd !== -1) {
        const breachedStationsString = bedrockSummary.slice(breachedStationsStart, breachedStationsEnd);
        try {
            const parseStations = JSON.parse(breachedStationsString);
            arrayStations = parseStations.breachedStations

        } catch (err) {
            console.error('Invalid breached stations format: breachedStations array not found')
        }
        
    } else {
        console.error('No breached stations found');
    }

    return [classificationSummary, arrayStations, mapPackClassification];
}

/**
 * Processes the result of release lambda
 * @param processReleaseOutput release lambda output
 * @returns boolean for if release lambda was successful or not, error message (only for failure case)
 */
export const processReleaseOutput = (processReleaseOutput: any) => {
    console.log(processReleaseOutput.errorMessage)
    if (processReleaseOutput && processReleaseOutput.errorMessage) {
        return [false, processReleaseOutput.errorMessage];
    }
    return [true];
}

/**
 * Creates a map of approval status items keyed by mapPackString
 * @param approvalStatus - The approval status data (array or object)
 * @returns A map of approval status items
 */
const createApprovalMap = (approvalStatus: ApprovalStatus): { [key: string]: ApprovalStatusItem } => {
    try {
        if (Array.isArray(approvalStatus) && approvalStatus.length > 0) {
            return approvalStatus.reduce((map: { [key: string]: ApprovalStatusItem }, item: ApprovalStatusItem) => {
                if (item && typeof item === 'object' && item.mapPackString && typeof item.mapPackString === 'string') {
                    map[item.mapPackString] = item;
                }
                return map;
            }, {});
        } else if (approvalStatus && typeof approvalStatus === 'object' && !Array.isArray(approvalStatus)) {
            return approvalStatus;
        }
        return {};
    } catch (error) {
        console.error('Error creating approval map:', error);
        return {};
    }
};

const RELEASE_STATUS_PRIORITY: { [key: string]: number } = {
    "BUILD_COMPLETED": 0,
    "READY_FOR_RELEASE": 1,
    "INITIATED_RELEASE": 2,
    "RELEASED": 3,
};

/**
 * Creates a map of release status items keyed by rsmVersion (extracted from stateId).
 * When duplicate rsmVersions exist, keeps the entry with the highest status priority,
 * then the latest validationCompletionEpochSeconds as a tiebreaker.
 * @param releaseStatus - The release status data array
 * @returns A map of release status items keyed by rsmVersion
 */
const createReleaseMap = (releaseStatus: ReleaseStatus): { [key: string]: ReleaseStatusItem } => {
    try {
        if (!Array.isArray(releaseStatus)) return {};

        return releaseStatus.reduce((map: { [key: string]: ReleaseStatusItem }, item: ReleaseStatusItem) => {
            if (item && typeof item === 'object' &&
                item.spsMetadata &&
                typeof item.spsMetadata === 'object' &&
                item.spsMetadata.stateId &&
                typeof item.spsMetadata.stateId === 'string') {
                // Extract rsmVersion from stateId (format: "rsmVersion-mwoTimestamp")
                const rsmVersion = item.spsMetadata.stateId.split('-')[0];
                const existingItem = map[rsmVersion];
                if (!existingItem) {
                    map[rsmVersion] = item;
                } else {
                    const existingPriority = RELEASE_STATUS_PRIORITY[existingItem.status] ?? -1;
                    const newPriority = RELEASE_STATUS_PRIORITY[item.status] ?? -1;
                    if (newPriority > existingPriority ||
                        (newPriority === existingPriority && item.validationCompletionEpochSeconds > existingItem.validationCompletionEpochSeconds)) {
                        map[rsmVersion] = item;
                    }
                }
            }
            return map;
        }, {});
    } catch (error) {
        console.error('Error creating release map:', error);
        return {};
    }
};

/**
 * Processes nested items to add approval status and calculate pending approvals
 * @param nestedItems - Array of validation nested items
 * @param approvalMap - Map of approval status items
 * @returns Object containing updated nested items and total pending approvals
 */
const processNestedItems = (
    nestedItems: ValidationNestedItem[], 
    approvalMap: { [key: string]: ApprovalStatusItem }
): { updatedNestedItems: ValidationNestedItem[], totalPendingApprovals: number } => {
    try {
        if (!Array.isArray(nestedItems)) {
            console.warn('Invalid nestedItems provided, returning empty array');
            return { updatedNestedItems: [], totalPendingApprovals: 0 };
        }

        let totalPendingApprovals = 0;
        
        const updatedNestedItems = nestedItems.map((subItem: ValidationNestedItem) => {
            if (!subItem || typeof subItem !== 'object') {
                console.warn('Invalid nested item found, skipping');
                return subItem;
            }

            const matchingApproval = approvalMap[subItem.mapPackVersion];
            const finalApprovalStatus = resolveApprovalStatus(matchingApproval);
            
            // Add to total pending approvals
            totalPendingApprovals += calculatePendingApprovals(matchingApproval);
            
            return {
                ...subItem,
                approvalStatus: finalApprovalStatus
            };
        });
        
        return { updatedNestedItems, totalPendingApprovals };
    } catch (error) {
        console.error('Error processing nested items:', error);
        return { updatedNestedItems: nestedItems, totalPendingApprovals: 0 };
    }
};

/**
 * Calculates approval counts from nested items
 * @param nestedItems - Array of validation nested items with approval status
 * @returns Object containing approved count and total approvers
 */
const calculateApprovalCounts = (nestedItems: ValidationNestedItem[]): { approvedCount: number, totalApprovers: number } => {
    const approvedCount = nestedItems.filter((subItem: ValidationNestedItem) => 
        subItem.approvalStatus === APPROVAL_STATUS.APPROVED
    ).length;
    
    return {
        approvedCount,
        totalApprovers: nestedItems.length
    };
};

/**
 * Gets release info (status and stateId) for a validation item by matching rsmVersion
 * @param item - Validation data item
 * @param releaseMap - Map of release status items keyed by rsmVersion
 * @returns Object with release status and the real stateId from the release API
 */
const getReleaseInfo = (item: ValidationDataItem, releaseMap: { [key: string]: ReleaseStatusItem }): { status: string, stateId: string } => {
    const releaseStatusItem = releaseMap[item.rsmVersion] || null;
    return {
        status: releaseStatusItem ? releaseStatusItem.status : APPROVAL_STATUS.BUILD_COMPLETED,
        stateId: releaseStatusItem ? releaseStatusItem.spsMetadata.stateId : '',
    };
};

/**
 * Combine approval status with release data to show on portal
 * @param validationData from mapPackValidation Lambda
 * @param approvalStatus from mapPackConsensus Lambda
 * @param releaseStatus from mapPackRelease Lambda
 * @returns combined data 
 */
export const addApprovalStatus = (
    validationData: ValidationData, 
    approvalStatus: ApprovalStatus, 
    releaseStatus: ReleaseStatus
): ValidationData => {
    if (!validationData?.length) return [];
    
    const approvalMap = createApprovalMap(approvalStatus);
    const releaseMap = createReleaseMap(releaseStatus);

    const updatedValidationData = validationData.map((item: ValidationDataItem) => {
        const updatedItem = { ...item };
        
        // Process nested items and get approval counts
        const { updatedNestedItems, totalPendingApprovals } = processNestedItems(item.nestedItems, approvalMap);
        const { approvedCount, totalApprovers } = calculateApprovalCounts(updatedNestedItems);
        
        // Update the item with processed data
        const releaseInfo = getReleaseInfo(item, releaseMap);
        updatedItem.nestedItems = updatedNestedItems;
        updatedItem.releaseStatus = releaseInfo.status;
        updatedItem.stateID = releaseInfo.stateId;
        updatedItem.approved = approvedCount;
        updatedItem.totalApprovers = totalApprovers;
        updatedItem.totalPendingApprovals = totalPendingApprovals;
        
        return updatedItem;
    });
    
    return updatedValidationData;
};
